import { type ClassType } from "remult";
import { remultStatic } from "./remult-static";

export type Class = ClassType<any>;

export type UnionToIntersection<U> = (
  U extends any ? (x: U) => void : never
) extends (x: infer I) => void
  ? I
  : never;

export type MergeClass<T extends Class> = (abstract new (
  ...args: any[]
) => UnionToIntersection<InstanceType<T>>) &
  UnionToIntersection<T>;

export function extender<T extends Class[]>(
  ...classes: T
): MergeClass<T[number]> {
  const undefinedIndices = classes
    .map((Cl, i) => (Cl == null ? i : -1))
    .filter((i) => i >= 0);
  if (undefinedIndices.length > 0) {
    console.warn(
      "[extender] One or more classes are undefined or null at index(es):",
      undefinedIndices.join(", "),
    );
  }
  const definedClasses = classes.filter(
    (Cl): Cl is NonNullable<typeof Cl> => Cl != null,
  );
  class MergedClass {
    constructor(...args: any[]) {
      definedClasses.forEach((Cl) => {
        const instance = new Cl(...args);
        Object.getOwnPropertyNames(instance).forEach((key) => {
          (this as any)[key] = instance[key];
        });
      });
    }
  }

  // Copy static methods
  definedClasses.forEach((Cl) => {
    Object.getOwnPropertyNames(Cl).forEach((key) => {
      if (key !== "prototype" && key !== "name" && key !== "length") {
        // Copy static properties/methods
        Object.defineProperty(
          MergedClass,
          key,
          Object.getOwnPropertyDescriptor(Cl, key) || {},
        );
      }
    });

    // Copy prototype methods
    Object.getOwnPropertyNames(Cl.prototype).forEach((key) => {
      if (key !== "constructor") {
        // Copy prototype methods
        Object.defineProperty(
          MergedClass.prototype,
          key,
          Object.getOwnPropertyDescriptor(Cl.prototype, key) || {},
        );
      }
    });

    // Copy metadata keys and values for decorators
    mergeRemultMetadata(definedClasses, MergedClass);
  });

  return MergedClass as any;
}

function mergeRemultMetadata(classes: Class[], targetClass: Class) {
  const mergedColumnsMap = new Map<string, any>();

  for (const Cl of classes) {
    const columns = remultStatic.columnsOfType.get(Cl);
    if (!columns) continue;

    for (const col of columns) {
      // Avoid duplicates by key, last wins or customize as needed
      mergedColumnsMap.set(col.key, col);
    }
  }

  // Convert merged map back to array
  const mergedColumns = Array.from(mergedColumnsMap.values());

  remultStatic.columnsOfType.set(targetClass, mergedColumns);
}
